<template>
    <b-container id="ai-assessment-page" fluid>
        <b-row>
            <b-col cols="12">
                <div class="border-bottom mb-3 pb-3">
                    <h3>AI 평가 관리</h3>
                    <p class="fw-400 text-secondary mb-0">
                        캠페인·업체별로 답변 중 AI 평가가 얼마나 이뤄졌는지 확인하고, 평가가 없거나 실패한 문항을 요청합니다.
                        개별 문항 확인·재요청은 [검토]에서 합니다.
                    </p>
                </div>
            </b-col>
        </b-row>

        <b-card no-body bg-variant="white" class="mb-3">
            <div class="p-3">
                <b-row>
                    <b-col cols="12" md="3" class="mb-3 mb-md-0">
                        <b-form-group label="평가 기간" class="mb-0">
                            <b-form-select v-model="selectedPeriod" :disabled="busy" @change="onPeriodChange">
                                <b-form-select-option v-for="p in periods" :key="p" :value="p">{{ p }}</b-form-select-option>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="5" class="mb-3 mb-md-0">
                        <b-form-group label="캠페인" class="mb-0">
                            <b-form-select v-model="selectedCampaignId" :disabled="busy || !campaigns.length" @change="onCampaignChange">
                                <b-form-select-option :value="null" disabled>
                                    {{ campaigns.length ? '캠페인을 선택하세요' : '해당 기간에 캠페인이 없습니다' }}
                                </b-form-select-option>
                                <b-form-select-option v-for="c in campaigns" :key="c.id" :value="c.id">
                                    {{ c.campaign_name }} ({{ c.id }})
                                </b-form-select-option>
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4">
                        <b-form-group label="업체명 검색" class="mb-0">
                            <b-form-input v-model="companyFilter" placeholder="업체명 일부" debounce="300" trim/>
                        </b-form-group>
                    </b-col>
                </b-row>
            </div>
        </b-card>

        <b-card no-body bg-variant="white">
            <template #header>
                <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: .5rem">
                    <div v-if="coverage" class="coverage-summary">
                        <span>업체 <b>{{ totals.companies }}</b></span>
                        <span>답변 문항 <b>{{ totals.answered }}</b></span>
                        <span>AI 평가 <b class="text-primary">{{ totals.ai_done }}</b> ({{ percent(totals.ai_done, totals.answered) }}%)</span>
                        <span>진행 중 <b>{{ totals.in_progress }}</b></span>
                        <span>실패 <b :class="{'text-danger': totals.failed}">{{ totals.failed }}</b></span>
                        <span>평가 없음 <b>{{ totals.missing }}</b></span>
                    </div>
                    <h6 v-else class="mb-0 text-muted">캠페인을 선택하면 업체별 AI 평가 현황이 표시됩니다.</h6>
                    <div v-if="coverage" class="d-flex align-items-center flex-wrap" style="gap: .75rem">
                        <div class="d-flex flex-column" style="max-width: 280px">
                            <b-form-checkbox v-model="rerunAll" switch size="sm" :disabled="busy" class="mb-0">
                                이미 평가된 문항도 다시 평가
                            </b-form-checkbox>
                            <small class="text-muted">켜면 AI 평가가 완료된 문항까지 전부 다시 요청합니다. AI 판단 기준이 바뀐 뒤 기존 결과를 새 기준으로 다시 받을 때만 사용하세요.</small>
                        </div>
                        <b-button size="sm" variant="outline-secondary" :disabled="busy" @click="loadCoverage">
                            <fa icon="sync-alt" class="mr-1"/>새로고침
                        </b-button>
                        <b-dropdown v-b-tooltip.hover="rerunAll ? '전체 다시 평가는 업체 단위로만 요청할 수 있습니다.' : null" size="sm" variant="primary"
                                    right
                                    :disabled="busy || !totals.requestable || rerunAll">
                            <template #button-content>
                                <b-spinner v-if="requestingCampaign" small class="mr-1"/>
                                캠페인 전체 AI 요청 ({{ totals.requestable }})
                            </template>
                            <b-dropdown-item @click="confirmCampaignRequest('immediate')">즉시</b-dropdown-item>
                            <b-dropdown-item @click="confirmCampaignRequest('batch')">배치 (최대 24시간)</b-dropdown-item>
                        </b-dropdown>
                        <small v-if="judgmentModel" class="text-warning">일괄 요청에는 모델 지정이 적용되지 않습니다.</small>
                    </div>
                </div>
            </template>

            <div class="p-3 border-bottom">
                <label for="company-judgment-model" class="mb-1">업체별 요청 판정 모델</label>
                <b-form-select id="company-judgment-model" v-model="judgmentModel" size="sm" aria-label="판정 모델"
                               :disabled="busy || judgmentModelsLoading" style="max-width: 480px">
                    <b-form-select-option :value="null">
                        {{ judgmentModelsLoading ? '판정 모델 목록을 불러오는 중...' : defaultJudgmentModelLabel }}
                    </b-form-select-option>
                    <b-form-select-option-group v-for="group in judgmentModelGroups" :key="group.provider" :label="group.label">
                        <b-form-select-option v-for="model in group.models" :key="model.id" :value="model.id">
                            {{ judgmentModelLabel(model) }}
                        </b-form-select-option>
                    </b-form-select-option-group>
                </b-form-select>
                <small v-if="selectedJudgmentModelNote" class="d-block text-muted mt-1">{{ selectedJudgmentModelNote }}</small>
                <small v-if="judgmentModelsError" class="d-block text-warning mt-1">판정 모델 목록을 불러오지 못했습니다. 기본값으로 요청할 수 있습니다.</small>
            </div>

            <b-table fixed responsive show-empty
                     :items="filteredCompanies"
                     :fields="fields"
                     :busy="loading"
                     class="mb-0"
            >
                <template #empty>
                    <div v-if="coverageError" class="text-center py-5">
                        <div class="text-danger mb-2">{{ coverageError }}</div>
                        <b-button size="sm" variant="outline-secondary" @click="loadCoverage">다시 시도</b-button>
                    </div>
                    <div v-else class="text-center text-muted py-5">
                        {{ coverage ? '표시할 업체가 없습니다.' : '캠페인을 선택하세요.' }}
                    </div>
                </template>
                <template #table-busy>
                    <div class="text-center text-primary py-5">
                        <b-spinner class="align-middle mr-2"/>AI 평가 현황을 불러오는 중...
                    </div>
                </template>
                <template #cell(company_name)="row">
                    {{ row.item.company_name || '-' }}
                    <span class="text-muted small">({{ row.item.company_id }})</span>
                </template>
                <template #cell(ai_done)="row">
                    {{ row.item.ai_done }} / {{ row.item.answered }}
                    <b-progress :max="row.item.answered || 1" height="4px" class="mt-1">
                        <b-progress-bar :value="row.item.ai_done" variant="primary"/>
                    </b-progress>
                </template>
                <template #cell(failed)="row">
                    <span :class="{'text-danger font-weight-bold': row.item.failed}">{{ row.item.failed }}</span>
                </template>
                <template #cell(last_requested_at)="row">
                    {{ row.item.last_requested_at ? row.item.last_requested_at.slice(0, 16) : '-' }}
                </template>
                <template #cell(actions)="row">
                    <div class="d-flex justify-content-end" style="gap: .25rem">
                            <b-dropdown size="sm" variant="outline-primary" right
                                        :disabled="busy || !rowRequestable(row.item)">
                                <template #button-content>
                                    <b-spinner v-if="requestingCompanyId === row.item.company_id" small class="mr-1"/>
                                    {{ rerunAll ? '전체 다시 평가' : '요청' }} ({{ rowRequestCount(row.item) }})
                                </template>
                                <b-dropdown-item @click="requestCompanyClick(row.item, 'immediate')">즉시</b-dropdown-item>
                                <b-dropdown-item @click="requestCompanyClick(row.item, 'batch')">배치 (최대 24시간)</b-dropdown-item>
                            </b-dropdown>
                            <b-button size="sm" variant="outline-secondary" :disabled="!row.item.project_code" @click="goReview(row.item)">
                                검토
                            </b-button>
                    </div>
                </template>
            </b-table>
        </b-card>
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
// AI 평가 관리 — 캠페인의 업체별 AI 평가 현황과 '평가 없거나 실패한 문항' 요청.
// 개별 AI 평가 확인/정정/대화/재요청은 답변 화면의 AI 검토 모드(AIEvaluationReviewWorkspace_v2)에서 한다.
import judgmentModelsMixin from '@/mixins/judgmentModels';
import {runConfirmedDispatch} from '@shared/utils/aiAuditDispatch';

const MODE_LABEL = {immediate: '즉시', batch: '배치'};

// 2024 상반기부터 올해 하반기까지 — 새 회차가 생겨도 배포 없이 선택 가능. 기본값은 현재 반기.
const NOW = new Date();
const PERIODS = [];
for (let y = 2024; y <= NOW.getFullYear(); y += 1) PERIODS.push(`${y}_1H`, `${y}_2H`);
const DEFAULT_PERIOD = '2026_1H';  // 기존 기본값 유지 — 선택지만 자동 생성

export default {
    name: 'AIAssessmentV2',
    mixins: [judgmentModelsMixin],
    data() {
        return {
            periods: PERIODS,
            selectedPeriod: DEFAULT_PERIOD,
            campaigns: [],
            selectedCampaignId: null,
            coverage: null,
            coverageError: '',
            companyFilter: '',
            rerunAll: false,
            loading: false,
            requestingCompanyId: null,
            requestingCampaign: false,
            initializing: true,
            fields: [
                {key: 'company_name', label: '업체명'},
                {key: 'answered', label: '답변 문항', thStyle: {width: '8%'}, class: 'text-right'},
                {key: 'ai_done', label: 'AI 평가', thStyle: {width: '12%'}},
                {key: 'in_progress', label: '진행 중', thStyle: {width: '7%'}, class: 'text-right'},
                {key: 'failed', label: '실패', thStyle: {width: '6%'}, class: 'text-right'},
                {key: 'missing', label: '평가 없음', thStyle: {width: '8%'}, class: 'text-right'},
                {key: 'last_requested_at', label: '최근 요청', thStyle: {width: '12%'}},
                {key: 'actions', label: '', thStyle: {width: '17%'}},
            ],
        };
    },
    computed: {
        busy() {
            return this.loading || this.requestingCompanyId !== null || this.requestingCampaign;
        },
        totals() {
            return this.coverage?.totals || {};
        },
        selectedCampaign() {
            return this.campaigns.find(c => c.id === this.selectedCampaignId) || null;
        },
        filteredCompanies() {
            const rows = this.coverage?.companies || [];
            const keyword = this.companyFilter.toLowerCase();
            return keyword ? rows.filter(r => (r.company_name || '').toLowerCase().includes(keyword)) : rows;
        },
    },
    watch: {
        companyFilter() {
            if (!this.initializing) this.syncQuery();
        },
    },
    created() {
        this.initFromQuery();
    },
    methods: {
        // 새로고침/링크 공유/뒤로가기로 기간·캠페인·업체 검색어를 복원한다. 값은 신뢰하지 않고 스칼라+화이트리스트로 검증.
        async initFromQuery() {
            const q = this.$route.query || {};
            const first = (v) => (Array.isArray(v) ? v[0] : v);
            const period = first(q.period);
            if (this.periods.includes(period)) this.selectedPeriod = period;
            this.companyFilter = (typeof first(q.company) === 'string' ? first(q.company) : '').slice(0, 200);
            try {
                await this.loadCampaigns();
                const raw = first(q.campaign_id);
                if (raw == null || raw === '') return;
                // 목록에 있는 캠페인만 적용 — 존재 확인 전에는 coverage 를 부르지 않는다
                const found = this.campaigns.find(c => String(c.id) === String(raw));
                if (!found) {
                    this.$EventBus.$emit('toast-warning', 'AI 평가 관리', '링크의 캠페인을 이 기간 목록에서 찾을 수 없습니다.');
                    return;
                }
                this.selectedCampaignId = found.id;
                await this.loadCoverage();
            } finally {
                this.initializing = false;
            }
        },
        syncQuery() {
            // replace: 히스토리를 늘리지 않고 현재 URL 만 갱신. rerunAll/judgmentModel 은 URL 에 넣지 않는다.
            const query = {period: this.selectedPeriod};
            if (this.selectedCampaignId != null) query.campaign_id = String(this.selectedCampaignId);
            const f = (this.companyFilter || '').trim();
            if (f) query.company = f;
            this.$router.replace({query}).catch(() => {});
        },
        async onPeriodChange() {
            this.selectedCampaignId = null;
            this.coverage = null;
            this.coverageError = '';
            await this.loadCampaigns();
            this.syncQuery();
        },
        async onCampaignChange() {
            this.syncQuery();
            await this.loadCoverage();
        },
        percent(part, whole) {
            return whole ? Math.round((part / whole) * 100) : 0;
        },
        rowRequestable(row) {
            return this.rerunAll ? (row.answered > 0 && !row.in_progress) : !!row.requestable;
        },
        rowRequestCount(row) {
            return this.rerunAll ? row.answered : row.requestable;
        },
        async loadCampaigns() {
            this.loading = true;
            try {
                const {data} = await this.$axios.get('/campaigns', {params: {period: this.selectedPeriod}});
                this.campaigns = [...((data && data.campaigns) || [])]
                    .sort((a, b) => Number(b.id) - Number(a.id));
            } catch (e) {
                this.campaigns = [];
                this.$EventBus.$emit('toast-failure', '조회 오류', '캠페인 목록을 불러오지 못했습니다.');
            } finally {
                this.loading = false;
            }
        },
        async loadCoverage() {
            const campaignId = this.selectedCampaignId;
            if (campaignId == null) return;
            // 이전 캠페인 표가 남아 새 캠페인으로 요청이 나가지 않도록 먼저 비운다
            this.coverage = null;
            this.coverageError = '';
            this.loading = true;
            try {
                const {data} = await this.$axios.get('/assessments/ai-audit/coverage', {params: {campaign_id: campaignId}});
                if (campaignId === this.selectedCampaignId) this.coverage = data;
            } catch (e) {
                if (campaignId === this.selectedCampaignId) this.coverageError = 'AI 평가 현황을 불러오지 못했습니다.';
                this.$EventBus.$emit('toast-failure', '조회 오류', 'AI 평가 현황을 불러오지 못했습니다.');
            } finally {
                this.loading = false;
            }
        },
        requestCompanyClick(row, judgmentMode) {
            if (this.busy) return;
            if (!this.rerunAll) return this.requestCompany(row, judgmentMode);
            const h = this.$createElement;
            this.$showModal({
                type: 'confirm',
                state: 'info',
                title: `전체 다시 평가 (${MODE_LABEL[judgmentMode]})`,
                message: h('div', {}, [
                    h('div', {}, `${row.company_name} 업체의 답변 문항 ${row.answered}건을 AI 평가 완료 여부와 관계없이 모두 다시 평가합니다.`),
                    h('div', {class: 'text-muted small mt-2'},
                        judgmentMode === 'batch'
                            ? '배치는 비용이 적게 들지만 결과까지 최대 24시간이 걸립니다.'
                            : '즉시 처리합니다.'),
                ]),
            }).then((ok) => {
                if (ok) this.requestCompany(row, judgmentMode);
            });
        },
        async requestCompany(row, judgmentMode) {
            if (this.busy) return;
            this.requestingCompanyId = row.company_id;
            try {
                const payload = {
                    campaign_id: this.selectedCampaignId,
                    company_id: row.company_id,
                    only_missing: !this.rerunAll,
                    judgment_mode: judgmentMode,
                };
                if (this.judgmentModel) payload.judgment_model = this.judgmentModel;
                const {data} = await this.$axios.post('/assessments/ai-audit', payload);
                const created = (data?.created || []).length;
                const errors = (data?.errors || []).length;
                const title = this.rerunAll ? 'AI 재평가 요청' : 'AI 평가 요청';
                if (errors) {
                    this.$EventBus.$emit('toast-failure', title, `${row.company_name}: ${created}건 요청, ${errors}건 실패`);
                } else if (this.rerunAll) {
                    this.$EventBus.$emit('toast-success', title, `${row.company_name}: ${created}건 재평가를 ${MODE_LABEL[judgmentMode]}로 요청했습니다.`);
                } else {
                    this.$EventBus.$emit('toast-success', title, `${row.company_name}: ${created}건을 ${MODE_LABEL[judgmentMode]}로 요청했습니다.`);
                }
            } catch (e) {
                const message = e?.response?.status === 400 && String(getErrorMessage(e, '')).includes('judgment_model')
                    ? '선택한 판정 모델을 사용할 수 없습니다. 드롭다운에서 다시 선택해주세요.'
                    : getErrorMessage(e, 'AI 평가 요청 중 오류가 발생했습니다.');
                this.$EventBus.$emit('toast-failure', 'AI 평가 요청 실패', message);
            } finally {
                this.requestingCompanyId = null;
            }
            await this.loadCoverage();
        },
        async confirmCampaignRequest(judgmentMode) {
            // 서버가 dry_run 으로 센 건수를 확인받고 그 건수(confirm_expected)로만 발행한다.
            // 버튼 숫자(coverage)는 접근 가능한 업체만 세므로 서버 건수와 다를 수 있어 모달에는 서버 건수만 쓴다.
            if (this.busy) return;
            this.requestingCampaign = true;
            try {
                const result = await runConfirmedDispatch({
                    post: async payload => (await this.$axios.post('/assessments/ai-audit/campaign', payload)).data,
                    body: {campaign_id: this.selectedCampaignId, judgment_mode: judgmentMode},
                    confirm: (expected, previous) => this.confirmCampaignModal(judgmentMode, expected, previous),
                });
                if (result.outcome === 'empty') {
                    this.$EventBus.$emit('toast-warning', 'AI 평가 일괄 요청', '요청할 대상이 없습니다.');
                } else if (result.outcome === 'accepted') {
                    const {data} = result;
                    if (data?.published === false) {
                        this.$EventBus.$emit('toast-warning', 'AI 평가 일괄 요청 발행 실패',
                            `${data?.expected ?? 0}건 요청은 접수됐지만 처리 작업을 시작하지 못했습니다. 개발팀에 문의해주세요.`);
                    } else {
                        this.$EventBus.$emit('toast-success', 'AI 평가 일괄 요청 접수',
                            `${data?.expected ?? 0}건 요청을 접수했습니다. 처리 상황은 [새로고침]으로 확인하세요.`);
                    }
                }
            } catch (e) {
                this.$EventBus.$emit('toast-failure', 'AI 평가 일괄 요청 실패',
                    getErrorMessage(e, '') || (!e?.isAxiosError && e?.message) || 'AI 평가 일괄 요청 중 오류가 발생했습니다.');
            } finally {
                this.requestingCampaign = false;
            }
            await this.loadCoverage();
        },
        confirmCampaignModal(judgmentMode, expected, previous) {
            const h = this.$createElement;
            return this.$showModal({
                type: 'confirm',
                state: previous == null ? 'info' : 'warning',
                title: `캠페인 전체 AI 요청 (${MODE_LABEL[judgmentMode]})`,
                message: h('div', {}, [
                    previous != null && h('div', {class: 'text-danger mb-2'},
                        `요청하는 사이 대상 건수가 바뀌었습니다 (약 ${previous}건 → 약 ${expected}건). 바뀐 건수로 다시 확인해주세요.`),
                    h('div', {}, `${this.selectedCampaign?.campaign_name || ''} 캠페인에서 AI 평가가 없거나 실패한 문항 약 ${expected}건을 요청합니다.`),
                    h('div', {class: 'text-muted small mt-2'},
                        judgmentMode === 'batch'
                            ? '배치는 비용이 적게 들지만 결과까지 최대 24시간이 걸립니다.'
                            : '즉시 처리합니다.'),
                ]),
            });
        },
        goReview(row) {
            // ai_review=1: 답변 화면이 AI 검토 모드로 열린다(문항은 화면에서 선택).
            this.$router.push({
                name: 'CompanyAnswers',
                params: {company_id: row.company_id},
                query: {
                    pc: row.project_code,
                    cmp: this.selectedCampaignId,
                    cpn: row.company_id,
                    cpnm: row.company_name,
                    template_code: this.selectedCampaign?.template_code,
                    admin_mode: 'project',
                    ai_review: '1',
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.coverage-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    font-size: .875rem;
}
</style>
